import { Component, Input } from '@angular/core';
import { FileModel, MessagingCallbackAction } from '@ca/core/models';
import { MessagingService } from '@ca/core/services';
import { TranslateService } from '@ngx-translate/core';
import { CziConverterService } from 'src/app/core/services/czi-converter.service';

@Component({
    selector: 'ca-czi-conversion-info',
    templateUrl: './czi-conversion-info.component.html',
    styleUrls: ['./czi-conversion-info.component.scss'],
})
export class CziConversionInfoComponent {
    @Input() isConversionInProgress: boolean;
    @Input() isDatasetPage?: boolean = false;
    @Input() files: FileModel[];
    @Input() messagingCallbackAction?: MessagingCallbackAction<{ operationId: string }>;
    @Input() isInvalidFilesNotificationVisible?: boolean;

    constructor(
        private cziConverterService: CziConverterService,
        private messagingService: MessagingService,
        private translateService: TranslateService,
    ) {}

    /**
     * Calculates the number of files that failed during the conversion process.
     * @returns The count of files with failed conversion status.
     */
    get numberOfConversionFailedFiles(): number {
        return this.files ? this.cziConverterService.getNumberOfConversionFailedFiles(this.files) : 0;
    }

    /**
     * Calculates the number of files currently being converted.
     * @returns The count of files in the "Active" conversion status.
     */
    get numberOfFilesBeingConverted(): number {
        return this.files ? this.cziConverterService.getNumberOfFilesBeingConverted(this.files) : 0;
    }

    /**
     * Generates a localized message for the ongoing conversion process.
     * @returns A string describing the current conversion progress.
     */
    get conversionInProgressMessage(): string {
        if (this.numberOfFilesBeingConverted > 0) {
            return this.numberOfFilesBeingConverted > 1
                ? this.translateService.instant('CZI_CONVERSION.CONVERTING_FILES_TO_CZI', {
                      numberOfCziFiles: this.numberOfFilesBeingConverted,
                  })
                : this.translateService.instant('CZI_CONVERSION.CONVERTING_FILE_TO_CZI');
        }
        return '';
    }

    /**
     * Generates a localized warning message for failed file conversions.
     * @returns A string describing the failed conversion count and status.
     */
    get failedConversionMessage(): string {
        if (this.numberOfConversionFailedFiles > 0) {
            return this.numberOfConversionFailedFiles > 1
                ? this.translateService.instant('CZI_CONVERSION.CONVERSION_OF_FILES_FAILED_WARNING', {
                      numberOfConversionFailedFiles: this.numberOfConversionFailedFiles,
                  })
                : this.translateService.instant('CZI_CONVERSION.CONVERSION_OF_FILE_FAILED_WARNING');
        }
        return '';
    }

    /**
     * Provides a tooltip for failed conversion warnings.
     * The tooltip message depends on whether the current page is a dataset page.
     * @returns A localized string for the tooltip.
     */
    get failedConversionTooltip(): string {
        return this.isDatasetPage
            ? this.translateService.instant('CZI_CONVERSION.CONVERSION_OF_FILES_FAILED_WARNING_TOOLTIP_DATASET')
            : this.translateService.instant('CZI_CONVERSION.CONVERSION_OF_FILES_FAILED_WARNING_TOOLTIP');
    }

    /**
     * Retries the conversion process for files with a failed conversion status.
     * Resets the conversion status for failed files and triggers a messaging callback.
     */
    retry(): void {
        if (this.messagingCallbackAction) {
            const failedFiles = this.files.filter(file => file.convertStatus !== 'Active' && file.convertStatus !== 'Success');
            failedFiles.forEach(file => (file.convertStatus = undefined));

            const { topicId, action, payload: { operationId } = {} } = this.messagingCallbackAction;
            this.messagingService.publish(topicId, { action, payload: { operationId, files: failedFiles } });
        }
    }
}
